import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import BadgeSizeLabel from "../components/BadgeSizeLabel";
import EventTemplatePreview from "./EventTemplatePreview";
import "./Events.css";

const formatDate = (date) => {
  if (!date) return "Not saved yet";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
  }).format(new Date(date));
};

const getAttendeeCount = (event) =>
  event.spreadsheetData?.fileData?.data?.length ?? 0;

const getAttendeeLabel = (count) =>
  `${count} attendee${count === 1 ? "" : "s"}`;

const EventCardDetails = ({ event, showAttendeeCount = true }) => (
  <div className="event-card-details">
    {showAttendeeCount && (
      <span className="event-card-detail-chip">{getAttendeeLabel(getAttendeeCount(event))}</span>
    )}
    <span className="event-card-detail-chip event-card-size-chip">
      {event.badgeSize ? (
        <BadgeSizeLabel size={event.badgeSize} />
      ) : (
        "Badge size not set"
      )}
    </span>
  </div>
);

const EventCardActions = ({ event, onEventDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleDocumentClick = (clickEvent) => {
      if (!menuRef.current?.contains(clickEvent.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (keyEvent) => {
      if (keyEvent.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="event-card-actions" ref={menuRef}>
      <button
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={`Open actions for ${event.name}`}
        className="event-card-menu-button"
        onClick={() => setIsOpen((value) => !value)}
        type="button"
      >
        <MoreHorizIcon fontSize="small" />
      </button>
      {isOpen && (
        <div className="event-card-actions-menu" role="menu">
          <button
            className="event-card-actions-menu-item event-card-actions-menu-item-danger"
            onClick={() => {
              setIsOpen(false);
              onEventDelete(event);
            }}
            role="menuitem"
            type="button"
          >
            <DeleteOutlineIcon fontSize="small" />
            Delete event
          </button>
        </div>
      )}
    </div>
  );
};

const EventCard = ({
  event,
  onEventDelete,
  onEventSelect,
  previewSpreadsheetData = null,
  showEventDetails = false,
  showEventOwner = false,
  showEventAttendeeCount = true,
  useFirstAttendeePreview = false,
}) => {
  const content = (
    <>
      <EventTemplatePreview
        badgeSize={event.badgeSize}
        spreadsheetData={previewSpreadsheetData || event.spreadsheetData}
        template={event.badgeTemplate}
        title={event.name}
        useFirstAttendee={useFirstAttendeePreview}
      />
      <div className="event-card-content">
        <h2>{event.name}</h2>
        <p className="event-meta">
          Created on {formatDate(event.createdAt)}
        </p>
        {showEventOwner && (
          <div className="event-owner">
            <span>{event.ownerEmail || "Unknown email"}</span>
            <span>User ID: {event.ownerId}</span>
          </div>
        )}
        {showEventDetails && (
          <EventCardDetails
            event={event}
            showAttendeeCount={showEventAttendeeCount}
          />
        )}
      </div>
    </>
  );

  if (onEventSelect) {
    return (
      <button
        className="event-card event-card-button"
        onClick={() => onEventSelect(event)}
        type="button"
      >
        {content}
      </button>
    );
  }

  if (onEventDelete) {
    return (
      <article className="event-card event-card-with-actions">
        <Link className="event-card-main-link" to={`/events/${event.id}`}>
          {content}
        </Link>
        <EventCardActions event={event} onEventDelete={onEventDelete} />
      </article>
    );
  }

  return (
    <Link className="event-card" to={`/events/${event.id}`}>
      {content}
    </Link>
  );
};

const EventCardsGrid = ({
  events,
  onEventDelete,
  onEventSelect,
  previewSpreadsheetData = null,
  showEventDetails = false,
  showEventOwner = false,
  showEventAttendeeCount = true,
  useFirstAttendeePreview = false,
  asFragment = false,
}) => {
  const cards = events.map((event) => (
    <EventCard
      event={event}
      key={event.id}
      onEventDelete={onEventDelete}
      onEventSelect={onEventSelect}
      previewSpreadsheetData={previewSpreadsheetData}
      showEventAttendeeCount={showEventAttendeeCount}
      showEventDetails={showEventDetails}
      showEventOwner={showEventOwner}
      useFirstAttendeePreview={useFirstAttendeePreview}
    />
  ));

  return asFragment ? cards : <div className="events-grid">{cards}</div>;
};

export default EventCardsGrid;
